import { useEffect, useMemo, useRef, useState } from 'react'

export type MediaItem = {
  kind?: string
  uri?: string
  mimeType?: string
  mime_type?: string
  poster?: string
  label?: string
  youtubeId?: string
  youtube_id?: string
  sourceField?: string
  source_field?: string
  position?: number | string
}

const clean = (value: unknown) => String(value || '').trim()

const PLACEHOLDER_MARKERS = [
  'addurlto.here', 'add-url-to.here', 'add_url_here', 'image_url_here',
  'audio_url_here', 'video_url_here', 'media_url_here', 'mp3_url_here',
  'animation_url_here', 'your_url_here', 'your-media-url', 'your_media_url',
  'placeholder_url', 'replace_with_url'
]

function isPlaceholderUri(value: unknown) {
  const text = clean(value).toLowerCase()
  return !!text && PLACEHOLDER_MARKERS.some(marker => text.includes(marker))
}

export function humanMediaKind(kind: unknown) {
  return ({
    image: 'Image', video: 'Video', audio: 'Audio', youtube: 'YouTube',
    model: '3D / model', external: 'External media'
  } as Record<string, string>)[clean(kind).toLowerCase()] || 'Media'
}

export function mediaKindIcon(kind: unknown) {
  return ({ image: '◫', video: '▶', audio: '♪', youtube: '▶', model: '◇', external: '↗' } as Record<string, string>)[clean(kind).toLowerCase()] || '•'
}

function mediaPriority(item: MediaItem) {
  const kind = clean(item.kind).toLowerCase()
  const uri = clean(item.uri).toLowerCase().split('#', 1)[0].split('?', 1)[0]
  const mime = clean(item.mimeType || item.mime_type).toLowerCase()
  const source = clean(item.sourceField || item.source_field).toLowerCase()
  const label = clean(item.label).toLowerCase()
  const isAudio = kind === 'audio' || mime.startsWith('audio/')
  const isMp3 = isAudio && (mime === 'audio/mpeg' || mime === 'audio/mp3' || uri.endsWith('.mp3'))
  const explicitAnimation = source.includes('animation') || source === 'animation_url' || label.includes('animated')
  const animatedImage = mime === 'image/gif' || mime === 'image/apng' || uri.endsWith('.gif') || uri.endsWith('.apng')
  if (isMp3) return 0
  if (isAudio) return 1
  if (explicitAnimation) return 2
  if (kind === 'video') return 3
  if (kind === 'youtube') return 4
  if (animatedImage) return 5
  if (kind === 'model') return 6
  if (kind === 'external') return 7
  if (kind === 'image') return 8
  return 9
}

export function normalizeMedia(media: MediaItem[] = [], fallbackImage = '', name = 'NFT') {
  const seen = new Set<string>()
  const valid: Array<{ item: MediaItem; originalIndex: number }> = []
  for (const [originalIndex, item] of (Array.isArray(media) ? media : []).entries()) {
    const uri = clean(item?.uri)
    const youtubeId = clean(item?.youtubeId || item?.youtube_id)
    if ((!uri && !youtubeId) || (uri && isPlaceholderUri(uri))) continue
    const key = (uri || `youtube:${youtubeId}`).split('#', 1)[0].toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    valid.push({ item, originalIndex })
  }
  if (!valid.length && fallbackImage && !isPlaceholderUri(fallbackImage)) {
    valid.push({ item: { kind: 'image', uri: fallbackImage, label: name, position: 0 }, originalIndex: 0 })
  }
  return valid
    .sort((a, b) => {
      const priority = mediaPriority(a.item) - mediaPriority(b.item)
      if (priority) return priority
      const ap = Number.isFinite(Number(a.item.position)) ? Number(a.item.position) : a.originalIndex
      const bp = Number.isFinite(Number(b.item.position)) ? Number(b.item.position) : b.originalIndex
      return ap - bp || a.originalIndex - b.originalIndex
    })
    .map(({ item }, displayPosition) => ({ ...item, displayPosition }))
}

function youtubeIdFor(item: MediaItem) {
  const direct = clean(item.youtubeId || item.youtube_id)
  if (direct) return direct
  const uri = clean(item.uri)
  return uri.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^?&/]+)/)?.[1] || ''
}

function Player({ item, poster, name, onFullscreen }: { item: MediaItem; poster: string; name: string; onFullscreen?: () => void }) {
  const kind = clean(item.kind).toLowerCase() || 'external'
  const uri = clean(item.uri)
  const mime = clean(item.mimeType || item.mime_type)
  const fallbackPoster = clean(item.poster || poster)
  const youtubeId = youtubeIdFor(item)

  if (kind === 'image') {
    return <div className={`r54-player r54-kind-${kind}`}><img className="r54-main-image" src={uri} alt={clean(item.label) || name} referrerPolicy="no-referrer" decoding="async" onClick={onFullscreen}/></div>
  }
  if (kind === 'video') {
    return <div className={`r54-player r54-kind-${kind}`}><video className="r54-video" key={uri} controls playsInline autoPlay loop muted defaultMuted preload="auto" poster={fallbackPoster || undefined}><source src={uri} type={mime || undefined}/></video></div>
  }
  if (kind === 'audio') {
    return <div className={`r54-player r54-kind-${kind}`}><div className="r54-audio-visual">{fallbackPoster&&<img src={fallbackPoster} alt={clean(item.label) || 'NFT artwork'} referrerPolicy="no-referrer"/>}<strong>{clean(item.label) || 'NFT audio'}</strong><audio controls preload="metadata"><source src={uri} type={mime || undefined}/></audio></div></div>
  }
  if (kind === 'youtube' && youtubeId) {
    const src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}?rel=0&playsinline=1&autoplay=1&mute=1&loop=1&playlist=${encodeURIComponent(youtubeId)}`
    return <div className={`r54-player r54-kind-${kind}`}><iframe className="r54-youtube" src={src} title={clean(item.label) || 'NFT YouTube media'} loading="lazy" referrerPolicy="strict-origin-when-cross-origin" allow="autoplay; accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen/></div>
  }
  return <div className={`r54-player r54-kind-${kind}`}><div className="r54-external-media">{fallbackPoster&&<img src={fallbackPoster} alt={clean(item.label) || 'NFT media poster'} referrerPolicy="no-referrer"/>}<strong>{clean(item.label) || humanMediaKind(kind)}</strong><span>{kind === 'model' ? 'Open this 3D/model asset in a compatible viewer.' : 'This media opens on its external host.'}</span>{uri&&<a className="r54-external-button" href={uri} target="_blank" rel="noopener noreferrer">Open media ↗</a>}</div></div>
}

export default function MediaGallery({ media = [], fallbackImage = '', name = 'NFT', externalUrl = '' }: { media?: MediaItem[]; fallbackImage?: string; name?: string; externalUrl?: string }) {
  const items = useMemo(() => normalizeMedia(media, fallbackImage, name), [media, fallbackImage, name])
  const [index, setIndex] = useState(0)
  const [fullscreen, setFullscreen] = useState(false)
  const touchX = useRef<number | null>(null)

  useEffect(() => setIndex(0), [name, items.length])
  useEffect(() => {
    if (!fullscreen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFullscreen(false)
      if (event.key === 'ArrowLeft') setIndex(value => (value - 1 + items.length) % items.length)
      if (event.key === 'ArrowRight') setIndex(value => (value + 1) % items.length)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [fullscreen, items.length])

  if (!items.length) return <div className="media-missing">No media</div>
  const current = items[Math.max(0, Math.min(index, items.length - 1))]
  const poster = fallbackImage
  const move = (delta: number) => setIndex(value => (value + delta + items.length) % items.length)
  const single = items.length < 2

  const touchStart = (event: React.TouchEvent) => { touchX.current = event.touches?.[0]?.clientX ?? null }
  const touchEnd = (event: React.TouchEvent) => {
    if (touchX.current == null || single) return
    const end = event.changedTouches?.[0]?.clientX ?? touchX.current
    const diff = end - touchX.current
    touchX.current = null
    if (Math.abs(diff) > 55) move(diff > 0 ? -1 : 1)
  }

  return <>
    <div className="r54-gallery r85-gallery-marker">
      <div className="r54-stage" onTouchStart={touchStart} onTouchEnd={touchEnd}>
        <button type="button" className="r54-chevron r54-prev" aria-label="Previous NFT media" disabled={single} style={{visibility:single?'hidden':'visible',pointerEvents:single?'none':undefined}} tabIndex={single?-1:0} onClick={()=>move(-1)}>‹</button>
        <div className="r54-current"><Player item={current} poster={poster} name={name} onFullscreen={()=>setFullscreen(true)}/></div>
        <button type="button" className="r54-chevron r54-next" aria-label="Next NFT media" disabled={single} style={{visibility:single?'hidden':'visible',pointerEvents:single?'none':undefined}} tabIndex={single?-1:0} onClick={()=>move(1)}>›</button>
      </div>
      <div className="r54-tools"><span className="r54-counter">{humanMediaKind(current.kind)} · {index + 1} / {items.length}</span><button type="button" className="r54-full-button" onClick={()=>setFullscreen(true)}>⛶ Fullscreen</button></div>
      <div className="r54-selector">{items.map((item,i)=><button key={`${clean(item.uri)||clean(item.youtubeId)||i}-${i}`} type="button" className={`r54-media-chip${i===index?' active':''}`} title={`${humanMediaKind(item.kind)} ${i+1} of ${items.length}`} onClick={()=>setIndex(i)}>{mediaKindIcon(item.kind)} {clean(item.label) || humanMediaKind(item.kind)}</button>)}</div>
      {clean(externalUrl)&&<div className="r54-external-row"><a className="r54-external-button" href={clean(externalUrl)} target="_blank" rel="noopener noreferrer">Open external experience ↗</a></div>}
    </div>
    {fullscreen&&<div id="onlyrose-r85-fullscreen" role="dialog" aria-modal="true" onMouseDown={event=>{if(event.target===event.currentTarget)setFullscreen(false)}}>
      <div className="r54-full-head"><span>{humanMediaKind(current.kind)} · {index + 1} / {items.length}</span><button type="button" className="r54-full-close" onClick={()=>setFullscreen(false)}>Close ✕</button></div>
      <div className="r54-full-stage" onTouchStart={touchStart} onTouchEnd={touchEnd}>
        <button type="button" className="r54-chevron r54-prev" aria-label="Previous NFT media" disabled={single} style={{visibility:single?'hidden':'visible',pointerEvents:single?'none':undefined}} tabIndex={single?-1:0} onClick={()=>move(-1)}>‹</button>
        <div className="r54-full-content"><Player item={current} poster={poster} name={name}/></div>
        <button type="button" className="r54-chevron r54-next" aria-label="Next NFT media" disabled={single} style={{visibility:single?'hidden':'visible',pointerEvents:single?'none':undefined}} tabIndex={single?-1:0} onClick={()=>move(1)}>›</button>
      </div>
    </div>}
  </>
}
